import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    selectFullReport,
    selectCategoryData,
    selectTaxwiseReport,
    selectPerformanceReport,
    selectFromDate,
    selectToDate,
    selectSelectedBranch,
    selectActiveTab,
    selectActiveLoading,
    selectActiveError,
    setActiveTab,
    fetchCategoryFullReport,
    fetchCategoryHourlyReport,
    fetchCategoryTaxwiseReport,
    fetchCategoryPerformanceReport,
    CATEGORY_REPORT_TABS
} from '../../store/slices/categoryWiseReportsSlice';
import ReportSummaryWithChart from './ReportSummaryWithChart';
import { exportStyledExcel, exportStyledExcelMultiSheet, buildReportFilterLines } from '../../utilities/excelExport';
import './billreports.css';

const getBranchId = (selectedBranch) => {
    const b = selectedBranch;
    return (b && (typeof b === 'object' ? b.branch_id : b)) || 'All';
};

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
    });
};

const parseNumber = (value) => parseFloat(value || 0) || 0;

const EmptyState = ({ message = 'No data available. Apply filters and click Apply to load.' }) => (
    <div className="flex flex-column align-items-center justify-content-center p-6">
        <i className="pi pi-inbox text-6xl text-color-secondary mb-3" />
        <p className="text-xl text-color-secondary">No category data available</p>
        <p className="text-color-secondary mt-2">{message}</p>
    </div>
);

const FullReportTab = ({ fullReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const { bills = [] } = fullReport;
    const hasData = Array.isArray(bills) && bills.length > 0;
    const allKeys = bills.length
        ? [
              ...Object.keys(bills[0]),
              ...Array.from(new Set(bills.slice(1).flatMap((row) => Object.keys(row || {})))).filter((k) => !(k in bills[0]))
          ]
        : [];

    const formatHeaderFromKey = (key) =>
        String(key || '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (ch) => ch.toUpperCase());

    const isZeroLike = (value) => {
        if (value === null || value === undefined) return false;
        const n = parseFloat(String(value).trim());
        return Number.isFinite(n) && n === 0;
    };

    const isNumericColumn = (field) => {
        const key = String(field || '').toLowerCase();
        if (key.includes('code') || key.includes('date')) return false;
        const values = bills.map((row) => row?.[field]).filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
        if (!values.length) return false;
        return values.every((v) => /^-?\d+(\.\d+)?$/.test(String(v).trim()));
    };

    const isCurrencyLikeField = (field) => {
        const key = String(field || '').toLowerCase();
        const strictNonCurrencyHints = ['percent', 'percentage', 'cgst', 'sgst', 'gst', 'quantity', 'qty'];
        if (strictNonCurrencyHints.some((hint) => key.includes(hint))) return false;
        if (key.includes('amount') || key.includes('amt')) return true;
        if (key.includes('tax')) return false;
        const currencyHints = ['rate', 'price', 'revenue', 'value', 'sale'];
        return currencyHints.some((hint) => key.includes(hint));
    };

    const tableColumns = allKeys.map((field) => {
        const numeric = isNumericColumn(field);
        const currencyLike = numeric && isCurrencyLikeField(field);
        return {
            field,
            header: formatHeaderFromKey(field),
            sortable: true,
            style: { minWidth: '120px', textAlign: numeric ? 'right' : 'left' },
            body: (row) => {
                const value = row?.[field];
                if (value === null || value === undefined || value === '') return '-';
                if (numeric) {
                    const formatted = parseNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2 });
                    return currencyLike ? `₹${formatted}` : formatted;
                }
                if (String(field).toLowerCase().includes('date') && /^\d{4}-\d{2}-\d{2}/.test(String(value))) {
                    const [y, m, d] = String(value).split('-');
                    return `${d}-${m}-${y}`;
                }
                return String(value);
            },
            hideIfAllZero: numeric
        };
    });

    const visibleTableColumns = tableColumns.filter((col) => {
        if (!col.hideIfAllZero) return true;
        return !bills.every((row) => isZeroLike(row?.[col.field]));
    });

    const columnsForExport = visibleTableColumns.map((col) => ({
        header: col.header,
        getValue: (row) => row?.[col.field],
        numeric: col.hideIfAllZero,
        skipSum: /percent/i.test(col.field)
    }));

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Category-wise report will show here once data is loaded (select dates, branch and apply, or click this tab to load)." />
            </Card>
        );
    }

    const exportToExcel = async () => {
        if (!hasData) return;
        await exportStyledExcel({
            fileName: `CategoryFullReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Category Full Report',
            title: 'Category Full Report (Date Range)',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns: columnsForExport,
            rows: bills
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text('Category Full Report (Date Range)', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        autoTable(doc, {
            startY: y + 2,
            head: [columnsForExport.map((c) => c.header)],
            body: bills.map((row) =>
                columnsForExport.map((col) => {
                    const value = col.getValue(row);
                    return typeof value === 'number' ? value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value ?? '');
                })
            ),
            styles: { fontSize: 7 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`CategoryFullReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    return (
        <div className="bill-reports-container">
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Full Report (Date Range)</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {(fromDate || toDate) && (
                            <span className="text-sm text-color-secondary">
                                {fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch && (selectedBranch.branch_name || selectedBranch) && ` • ${selectedBranch.branch_name || selectedBranch}`}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>

                <DataTable
                    value={bills}
                    dataKey={allKeys[0] || 'id'}
                    showGridlines
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords} categories"
                    tableStyle={{ minWidth: '40rem' }}
                    className="p-datatable-sm"
                    emptyMessage="No category rows"
                >
                    {visibleTableColumns.map((col) => (
                        <Column key={col.field} field={col.field} header={col.header} body={col.body} style={col.style} sortable={col.sortable} />
                    ))}
                </DataTable>
            </Card>
        </div>
    );
};

const HourlyReportTab = ({ categoryData, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const [categorySearch, setCategorySearch] = useState('');
    const [categorySortOrder, setCategorySortOrder] = useState(1); // 1 = A→Z, -1 = Z→A
    const [selectedCategories, setSelectedCategories] = useState([]); // multiple selection, order = pin-to-top order

    const getTimeRangeFromRow = (row) => row.time_range ?? row.Time_Range ?? row.time_slot ?? row.hour_range ?? row.time_range_label ?? '';

    const normalizedHourlyByCategory = useMemo(() => {
        if (!categoryData || typeof categoryData !== 'object') return {};
        const out = {};
        Object.keys(categoryData).forEach((key) => {
            const rows = Array.isArray(categoryData[key]) ? categoryData[key] : categoryData[key]?.rows ?? [];
            if (!Array.isArray(rows) || rows.length === 0) return;
            out[key] = rows.map((row) => ({
                ...row,
                time_range: getTimeRangeFromRow(row)
            }));
        });
        return out;
    }, [categoryData]);

    const categoryNames = useMemo(() => {
        const names = Object.keys(normalizedHourlyByCategory).sort((a, b) => a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' }));
        return categorySortOrder === 1 ? names : names.slice().reverse();
    }, [normalizedHourlyByCategory, categorySortOrder]);

    const filteredCategoryNames = useMemo(() => {
        if (!categorySearch) return categoryNames;
        return categoryNames.filter((name) => name.toLowerCase().includes(categorySearch.toLowerCase()));
    }, [categoryNames, categorySearch]);

    const getTimeSortKey = (label) => {
        if (!label || typeof label !== 'string') return Number.MAX_SAFE_INTEGER;
        const [startPart] = label.split('-');
        const part = (startPart || '').trim().toLowerCase();
        const match = part.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
        if (!match) return Number.MAX_SAFE_INTEGER - 1;
        let hour = parseInt(match[1], 10);
        const minute = parseInt(match[2], 10) || 0;
        const meridiem = match[3];
        if (meridiem === 'pm' && hour < 12) hour += 12;
        if (meridiem === 'am' && hour === 12) hour = 0;
        return hour * 60 + minute;
    };

    const allTimeRanges = useMemo(() => {
        const set = new Set();
        Object.values(normalizedHourlyByCategory).forEach((rows) => {
            (rows || []).forEach((row) => {
                const tr = row.time_range;
                if (tr) set.add(tr);
            });
        });
        return Array.from(set).sort((a, b) => getTimeSortKey(a) - getTimeSortKey(b));
    }, [normalizedHourlyByCategory]);

    const selectedTotalByTimeRange = useMemo(() => {
        const totals = {};
        allTimeRanges.forEach((tr) => {
            totals[tr] = { sales: 0, qty: 0 };
        });
        selectedCategories.forEach((name) => {
            const rows = normalizedHourlyByCategory[name] || [];
            rows.forEach((row) => {
                if (row.time_range && totals[row.time_range]) {
                    totals[row.time_range].sales += parseFloat(row.Total_Hourly_Sales || 0);
                    totals[row.time_range].qty += parseInt(row.Total_Hourly_qty || 0, 10) || 0;
                }
            });
        });
        return totals;
    }, [selectedCategories, normalizedHourlyByCategory, allTimeRanges]);

    const pivotRows = useMemo(() => {
        const selectedOrdered = selectedCategories.filter((n) => filteredCategoryNames.includes(n));
        const rest = filteredCategoryNames.filter((n) => !selectedCategories.includes(n));
        const orderedNames = [...selectedOrdered, ...rest];
        const dataRows = orderedNames.map((name) => {
            const base = { category_name: name };
            const rows = normalizedHourlyByCategory[name] || [];
            rows.forEach((row) => {
                if (row.time_range) {
                    base[row.time_range] = row;
                }
            });
            return base;
        });
        if (selectedCategories.length === 0) return dataRows;
        const summaryRow = {
            category_name: 'Total (Selected)',
            _isSummaryRow: true
        };
        allTimeRanges.forEach((tr) => {
            summaryRow[tr] = {
                Total_Hourly_Sales: selectedTotalByTimeRange[tr]?.sales ?? 0,
                Total_Hourly_qty: selectedTotalByTimeRange[tr]?.qty ?? 0
            };
        });
        return [summaryRow, ...dataRows];
    }, [filteredCategoryNames, normalizedHourlyByCategory, selectedCategories, selectedTotalByTimeRange, allTimeRanges]);

    const grandTotalByTimeRange = useMemo(() => {
        const totals = {};
        allTimeRanges.forEach((tr) => {
            totals[tr] = { sales: 0, qty: 0 };
        });
        pivotRows
            .filter((row) => !row._isSummaryRow)
            .forEach((row) => {
                allTimeRanges.forEach((tr) => {
                    const slot = row[tr];
                    if (slot) {
                        totals[tr].sales += parseFloat(slot.Total_Hourly_Sales || 0);
                        totals[tr].qty += parseInt(slot.Total_Hourly_qty || 0, 10) || 0;
                    }
                });
            });
        return totals;
    }, [pivotRows, allTimeRanges]);

    const hasData = categoryNames.length > 0 && allTimeRanges.length > 0;

    const categoryHeaderTemplate = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => setCategorySortOrder((prev) => (prev === 1 ? -1 : 1))}>
            <span>Category</span>
            <i className={`pi ${categorySortOrder === 1 ? 'pi-sort-alpha-down' : 'pi-sort-alpha-up'}`} />
        </span>
    );

    const renderTimeCell = (slotRow) => {
        if (!slotRow) return '-';
        const sales = `₹${formatCurrency(slotRow.Total_Hourly_Sales)}`;
        const qty = slotRow.Total_Hourly_qty ?? 0;
        const split = slotRow.splitted || {};
        const tooltipParts = [
            'Split details for this time slot:',
            `NC Qty: ${split.nc_qty ?? 0}`,
            `NC Sales: ₹${formatCurrency(split.nc_sales)}`,
            `Discount Qty: ${split.discount_qty ?? 0}`,
            `Discount Sales: ₹${formatCurrency(split.discount_sales)}`,
            `Actual Qty: ${split.actual_qty ?? 0}`,
            `Actual Sales: ₹${formatCurrency(split.Actival_Sales)}`
        ];
        const tooltip = tooltipParts.join('\n');
        return (
            <div className="hourly-slot-cell" data-pr-tooltip={tooltip} data-pr-position="top" style={{ lineHeight: 1.3, cursor: 'pointer' }}>
                <div>{sales}</div>
                <div style={{ fontSize: '0.8rem' }}>Qty: {qty}</div>
            </div>
        );
    };

    const exportToExcel = async () => {
        if (!hasData) return;
        const rows = pivotRows.filter((row) => !row._isSummaryRow);
        const columns = [
            { header: 'Category', getValue: (r) => r.category_name || '-' },
            ...allTimeRanges.flatMap((tr) => [
                { header: `${tr} Sales`, getValue: (r) => parseNumber(r[tr]?.Total_Hourly_Sales), numeric: true },
                { header: `${tr} Qty`, getValue: (r) => parseNumber(r[tr]?.Total_Hourly_qty), numeric: true }
            ])
        ];
        await exportStyledExcel({
            fileName: `CategoryHourlyReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Category Hourly Report',
            title: 'Category Hourly Report',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns,
            rows
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('l', 'mm', 'a3');
        doc.setFontSize(14);
        doc.text('Category Hourly Report', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        const headers = ['Category'];
        allTimeRanges.forEach((tr) => {
            headers.push(`${tr} Sales`);
            headers.push(`${tr} Qty`);
        });
        const body = pivotRows
            .filter((row) => !row._isSummaryRow)
            .map((row) => {
                const values = [row.category_name];
                allTimeRanges.forEach((tr) => {
                    values.push(formatCurrency(row[tr]?.Total_Hourly_Sales));
                    values.push(parseNumber(row[tr]?.Total_Hourly_qty).toString());
                });
                return values;
            });
        autoTable(doc, {
            startY: y + 2,
            head: [headers],
            body,
            styles: { fontSize: 6 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`CategoryHourlyReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Hourly report will show here once data is loaded. Select Hourly Sales tab, apply filters and click Apply." />
            </Card>
        );
    }

    return (
        <div className="bill-reports-container">
            <Card>
                <Tooltip key={`${categorySearch}-${pivotRows.length}-${categorySortOrder}-${selectedCategories.length}`} target=".hourly-slot-cell" />
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Category-wise hourly sales</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {hasData && <Tag value={`${categoryNames.length} categories • ${allTimeRanges.length} time ranges`} severity="info" />}
                        {(fromDate || toDate) && (
                            <span className="text-sm text-color-secondary">
                                {fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch?.branch_name ? ` • ${selectedBranch.branch_name}` : ''}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <div className="text-xs text-color-secondary mb-2">
                    Each cell shows <strong>Total Hourly Sales</strong> and <strong>Qty</strong>. Hover a cell to view full split details for that time slot.
                </div>
                <div className="mb-3 flex align-items-center gap-2 flex-wrap">
                    <div className="max-w-20rem" style={{ position: 'relative', minWidth: '12rem' }}>
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search" />
                            <InputText value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} placeholder="Search categories..." className="w-full" style={{ paddingRight: categorySearch ? '2rem' : undefined }} />
                        </span>
                        {categorySearch && (
                            <span
                                role="button"
                                tabIndex={0}
                                style={{
                                    position: 'absolute',
                                    right: '0.75rem',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                    width: '1.25rem',
                                    height: '1.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.875rem'
                                }}
                                onClick={() => setCategorySearch('')}
                                onKeyDown={(e) => e.key === 'Enter' && setCategorySearch('')}
                                aria-label="Clear search"
                            >
                                <i className="pi pi-times" />
                            </span>
                        )}
                    </div>
                    {selectedCategories.length > 0 && <Button label="Clear all" icon="pi pi-times-circle" onClick={() => setSelectedCategories([])} size="small" style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }} />}
                </div>
                <DataTable
                    value={pivotRows}
                    dataKey={(row) => (row._isSummaryRow ? '_summary_' : row.category_name)}
                    showGridlines
                    scrollable
                    scrollDirection="both"
                    stripedRows
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm hourly-pivot-table"
                    emptyMessage="No matching categories"
                    onRowClick={(e) => {
                        if (e.data._isSummaryRow) return;
                        const name = e.data.category_name;
                        setSelectedCategories((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
                    }}
                    rowClassName={(rowData) => (rowData._isSummaryRow ? 'hourly-selected-total-row' : selectedCategories.includes(rowData.category_name) ? 'hourly-selected-row' : '')}
                    rowStyle={(rowData) => (rowData._isSummaryRow ? { cursor: 'default' } : { cursor: 'pointer' })}
                >
                    <Column
                        field="category_name"
                        header={categoryHeaderTemplate}
                        frozen
                        style={{ minWidth: '220px', fontWeight: '600' }}
                        footer={isManagerView ? null :
                            <div style={{ lineHeight: 1.3, textAlign: 'center', fontWeight: 700, color: 'var(--primary-color)' }}>
                                <div>Total</div>
                            </div>
                        }
                        body={(row) => (row._isSummaryRow ? <span style={{ fontWeight: 700 }}>Total (Selected)</span> : <span style={{ fontWeight: 600 }}>{row.category_name}</span>)}
                    />
                    {allTimeRanges.map((tr) => (
                        <Column
                            key={tr}
                            header={tr}
                            style={{ minWidth: '150px', textAlign: 'right' }}
                            footer={isManagerView ? null :
                                grandTotalByTimeRange[tr] ? (
                                    <div style={{ lineHeight: 1.3, textAlign: 'center', fontWeight: 600, color: 'var(--primary-color)' }}>
                                        <div>{`₹${formatCurrency(grandTotalByTimeRange[tr].sales)}`}</div>
                                        <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{`Qty: ${grandTotalByTimeRange[tr].qty}`}</div>
                                    </div>
                                ) : (
                                    ''
                                )
                            }
                            body={(row) =>
                                row._isSummaryRow ? (
                                    <div style={{ lineHeight: 1.3 }}>
                                        <div>{`₹${formatCurrency(row[tr]?.Total_Hourly_Sales || 0)}`}</div>
                                        <div style={{ fontSize: '0.8rem' }}>{`Qty: ${row[tr]?.Total_Hourly_qty || 0}`}</div>
                                    </div>
                                ) : (
                                    renderTimeCell(row[tr])
                                )
                            }
                        />
                    ))}
                </DataTable>
            </Card>
        </div>
    );
};

const TaxwiseReportTab = ({ taxwiseReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const hasData = Array.isArray(taxwiseReport) && taxwiseReport.length > 0;

    const gstHalf = (row, expectedPct) => {
        if (parseFloat(row.tax_percentage || 0) !== expectedPct) return '';
        return `₹${formatCurrency(parseNumber(row.tax_amount) / 2)}`;
    };
    const gstHalfNum = (row, expectedPct) =>
        parseFloat(row.tax_percentage || 0) !== expectedPct ? 0 : parseNumber(row.tax_amount) / 2;

    const columnsForExport = [
        { header: 'Category', getValue: (r) => r.category_name || '-' },
        { header: 'Total Qty', getValue: (r) => parseNumber(r.total_qty), numeric: true },
        { header: 'Total Sales', getValue: (r) => parseNumber(r.total_amount), numeric: true },
        { header: 'CGST 0%', getValue: (r) => gstHalfNum(r, 0), numeric: true },
        { header: 'SGST 0%', getValue: (r) => gstHalfNum(r, 0), numeric: true },
        { header: 'CGST 2.5%', getValue: (r) => gstHalfNum(r, 5), numeric: true },
        { header: 'SGST 2.5%', getValue: (r) => gstHalfNum(r, 5), numeric: true },
        { header: 'CGST 6%', getValue: (r) => gstHalfNum(r, 12), numeric: true },
        { header: 'SGST 6%', getValue: (r) => gstHalfNum(r, 12), numeric: true },
        { header: 'CGST 9%', getValue: (r) => gstHalfNum(r, 18), numeric: true },
        { header: 'SGST 9%', getValue: (r) => gstHalfNum(r, 18), numeric: true },
        { header: 'Total GST', getValue: (r) => parseNumber(r.tax_amount), numeric: true }
    ];

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Tax-wise report will show here once data is loaded. Select Tax-wise tab, apply filters and click Apply." />
            </Card>
        );
    }

    const exportToExcel = async () => {
        if (!hasData) return;
        await exportStyledExcel({
            fileName: `CategoryTaxwiseReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Category Taxwise Report',
            title: 'Category Taxwise Report',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns: columnsForExport,
            rows: taxwiseReport
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text('Category Tax-wise Report', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        autoTable(doc, {
            startY: y + 2,
            head: [columnsForExport.map((c) => c.header)],
            body: taxwiseReport.map((row) => columnsForExport.map((col) => formatCurrency(col.getValue(row)))),
            styles: { fontSize: 7 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`CategoryTaxwiseReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    return (
        <div className="bill-reports-container">
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Tax-wise Sales</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {(fromDate || toDate) && (
                            <span className="text-sm text-color-secondary">
                                {fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch && (selectedBranch.branch_name || selectedBranch) && ` • ${selectedBranch.branch_name || selectedBranch}`}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <DataTable value={taxwiseReport} dataKey="category_name" showGridlines paginator rows={10} rowsPerPageOptions={[10, 25, 50]} scrollable scrollDirection="both" tableStyle={{ minWidth: '60rem' }} className="p-datatable-sm">
                    <Column field="category_name" header="Category" sortable />
                    <Column field="total_qty" header="Total Qty" style={{ textAlign: 'right' }} />
                    <Column field="total_amount" header="Total Sales" body={(r) => `₹${formatCurrency(r.total_amount)}`} style={{ textAlign: 'right' }} />
                    <Column header="CGST 0%" body={(r) => gstHalf(r, 0)} style={{ textAlign: 'right' }} />
                    <Column header="SGST 0%" body={(r) => gstHalf(r, 0)} style={{ textAlign: 'right' }} />
                    <Column header="CGST 2.5%" body={(r) => gstHalf(r, 5)} style={{ textAlign: 'right' }} />
                    <Column header="SGST 2.5%" body={(r) => gstHalf(r, 5)} style={{ textAlign: 'right' }} />
                    <Column header="CGST 6%" body={(r) => gstHalf(r, 12)} style={{ textAlign: 'right' }} />
                    <Column header="SGST 6%" body={(r) => gstHalf(r, 12)} style={{ textAlign: 'right' }} />
                    <Column header="CGST 9%" body={(r) => gstHalf(r, 18)} style={{ textAlign: 'right' }} />
                    <Column header="SGST 9%" body={(r) => gstHalf(r, 18)} style={{ textAlign: 'right' }} />
                    <Column field="tax_amount" header="Total GST" body={(r) => `₹${formatCurrency(r.tax_amount)}`} style={{ textAlign: 'right' }} />
                </DataTable>
            </Card>
        </div>
    );
};

const PerformanceReportTab = ({ performanceReport, fromDate, toDate, selectedBranch, isManagerView = false }) => {
    const { top_selling = [], bottom_selling = [], report_period } = performanceReport || {};
    const hasData = (Array.isArray(top_selling) && top_selling.length > 0) || (Array.isArray(bottom_selling) && bottom_selling.length > 0);

    if (!hasData) {
        return (
            <Card>
                <EmptyState message="Performance ranking will show here once data is loaded. Select Performance Ranking tab, apply filters and click Apply." />
            </Card>
        );
    }

    const performanceColumns = [
        { header: 'Category', getValue: (r) => r.category_name || '-' },
        { header: 'Total Qty', getValue: (r) => parseNumber(r.total_qty), numeric: true },
        { header: 'Total Revenue', getValue: (r) => parseNumber(r.total_amount), numeric: true }
    ];

    const exportToExcel = async () => {
        if (!hasData) return;
        const filterLines = buildReportFilterLines({ fromDate, toDate, selectedBranch });
        await exportStyledExcelMultiSheet({
            fileName: `CategoryPerformanceReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheets: [
                { sheetName: 'Top Selling', title: 'Category Performance – Top Selling', filterLines, columns: performanceColumns, rows: top_selling },
                { sheetName: 'Bottom Selling', title: 'Category Performance – Bottom Selling', filterLines, columns: performanceColumns, rows: bottom_selling }
            ]
        });
    };

    const exportToPDF = () => {
        if (!hasData) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text('Category Performance Ranking', 14, 15);
        doc.setFontSize(9);
        let y = 23;
        if (report_period?.from && report_period?.to) {
            doc.text(`Period: ${report_period.from} to ${report_period.to}`, 14, y);
            y += 5;
        } else if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, y);
            y += 5;
        }
        if (selectedBranch && (selectedBranch.branch_name || selectedBranch)) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, y);
            y += 5;
        }
        doc.setFontSize(11);
        doc.text('Top Selling', 14, y + 2);
        autoTable(doc, {
            startY: y + 5,
            head: [['Category', 'Total Qty', 'Total Revenue']],
            body: top_selling.map((r) => [r.category_name || '-', parseNumber(r.total_qty).toString(), formatCurrency(r.total_amount)]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        const nextY = (doc.lastAutoTable?.finalY || y + 30) + 8;
        doc.setFontSize(11);
        doc.text('Bottom Selling', 14, nextY);
        autoTable(doc, {
            startY: nextY + 3,
            head: [['Category', 'Total Qty', 'Total Revenue']],
            body: bottom_selling.map((r) => [r.category_name || '-', parseNumber(r.total_qty).toString(), formatCurrency(r.total_amount)]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 122, 255] }
        });
        doc.save(`CategoryPerformanceReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    return (
        <div className="bill-reports-container">
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Performance Ranking</h3>
                    <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                        {(report_period?.from || fromDate) && (
                            <span className="text-sm text-color-secondary">
                                {report_period?.from && report_period?.to
                                    ? `${report_period.from} – ${report_period.to}`
                                    : fromDate && toDate
                                    ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${
                                          typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate
                                      }`
                                    : ''}
                                {selectedBranch && (selectedBranch.branch_name || selectedBranch) && ` • ${selectedBranch.branch_name || selectedBranch}`}
                            </span>
                        )}
                        {!isManagerView && <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={exportToExcel} disabled={!hasData} />}
                        {!isManagerView && <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger p-button-sm" onClick={exportToPDF} disabled={!hasData} />}
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12 lg:col-6">
                        <h4 style={{ textAlign: 'center', margin: '0 0 0.5rem 0' }}>Top Selling</h4>
                        <DataTable value={top_selling} dataKey="category_name" showGridlines paginator rows={10} className="p-datatable-sm">
                            <Column field="category_name" header="Category" sortable />
                            <Column field="total_qty" header="Total Qty" style={{ textAlign: 'right' }} />
                            <Column field="total_amount" header="Total Revenue" body={(r) => `₹${formatCurrency(r.total_amount)}`} style={{ textAlign: 'right' }} />
                        </DataTable>
                    </div>
                    <div className="col-12 lg:col-6">
                        <h4 style={{ textAlign: 'center', margin: '0 0 0.5rem 0' }}>Bottom Selling</h4>
                        <DataTable value={bottom_selling} dataKey="category_name" showGridlines paginator rows={10} className="p-datatable-sm">
                            <Column field="category_name" header="Category" sortable />
                            <Column field="total_qty" header="Total Qty" style={{ textAlign: 'right' }} />
                            <Column field="total_amount" header="Total Revenue" body={(r) => `₹${formatCurrency(r.total_amount)}`} style={{ textAlign: 'right' }} />
                        </DataTable>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const CategoryWiseReportsContent = ({ isManagerView = false }) => {
    const dispatch = useDispatch();
    const fullReport = useSelector(selectFullReport);
    const categoryData = useSelector(selectCategoryData);
    const taxwiseReport = useSelector(selectTaxwiseReport);
    const performanceReport = useSelector(selectPerformanceReport);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    const activeTab = useSelector(selectActiveTab);
    const loading = useSelector(selectActiveLoading);
    const error = useSelector(selectActiveError);

    const handleTabChange = (e) => {
        const index = e.index;
        dispatch(setActiveTab(index));
        if (fromDate && toDate) {
            const branchId = getBranchId(selectedBranch);
            if (index === CATEGORY_REPORT_TABS.FULL) dispatch(fetchCategoryFullReport({ fromDate, toDate, branchId }));
            else if (index === CATEGORY_REPORT_TABS.HOURLY) dispatch(fetchCategoryHourlyReport({ fromDate, toDate, branchId }));
            else if (index === CATEGORY_REPORT_TABS.TAXWISE) dispatch(fetchCategoryTaxwiseReport({ fromDate, toDate, branchId }));
            else if (index === CATEGORY_REPORT_TABS.PERFORMANCE) dispatch(fetchCategoryPerformanceReport({ fromDate, toDate, branchId }));
        }
    };

    const showSummaryCard = !isManagerView && fullReport?.summary && Object.keys(fullReport.summary).length > 0;

    return (
        <div className="bill-reports-container">
            {error && (
                <div
                    className="mb-3 p-3 border-round"
                    style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#b91c1c'
                    }}
                >
                    <span>{error}</span>
                </div>
            )}
            {showSummaryCard && (
                <ReportSummaryWithChart
                    summary={fullReport.summary}
                    fromDate={fromDate}
                    toDate={toDate}
                    selectedBranch={selectedBranch}
                    title="Category Summary"
                    subtitle="Overview of category-wise sales for the period"
                />
            )}

            <div className="report-data-tabs">
                <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
                    <TabPanel header="Full Report (Date Range)">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <FullReportTab fullReport={fullReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                    <TabPanel header="Hourly Sales">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <HourlyReportTab categoryData={categoryData} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                    <TabPanel header="Tax-wise Sales">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <TaxwiseReportTab taxwiseReport={taxwiseReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                    <TabPanel header="Performance Ranking">
                        {loading ? (
                            <Card>
                                <div className="flex justify-content-center align-items-center p-6">
                                    <ProgressSpinner />
                                </div>
                            </Card>
                        ) : (
                            <PerformanceReportTab performanceReport={performanceReport} fromDate={fromDate} toDate={toDate} selectedBranch={selectedBranch} isManagerView={isManagerView} />
                        )}
                    </TabPanel>
                </TabView>
            </div>
        </div>
    );
};

export default CategoryWiseReportsContent;
